/**
 * TiersPicker — sélecteur réutilisable dans l'annuaire partagé (mig 213).
 *
 * Recherche + filtre par casquette. Utilisé par les briques (ex. le devis vocal
 * du service technique filtre sur `fournisseur_prestataire`). Permet aussi de
 * CRÉER un nouveau tiers à la volée si `autoriserCreation` (décision D7 : le
 * service technique peut créer un fournisseur lui-même).
 *
 * Ne manipule jamais l'IBAN (l'annuaire partagé ne le renvoie pas).
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, Plus, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { Button, Input, Field } from '@/components/ui';
import type { AnnuaireTiers, TiersCategorieUsage } from '@roronoa-collectivites/shared-types';
import { TIERS_CATEGORIES_USAGE } from '@roronoa-collectivites/shared-types';

export function TiersPicker({
  categorie,
  selectedId,
  onSelect,
  autoriserCreation = false,
  categorieCreation,
}: {
  /** Filtre par défaut (ex. 'fournisseur_prestataire' pour un devis). */
  categorie?: TiersCategorieUsage;
  selectedId?: string | null;
  onSelect: (tiers: AnnuaireTiers) => void;
  autoriserCreation?: boolean;
  /** Casquette(s) données au tiers créé (défaut = `categorie` ou fournisseur). */
  categorieCreation?: TiersCategorieUsage[];
}) {
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState<TiersCategorieUsage | ''>(categorie ?? '');
  const [items, setItems] = useState<AnnuaireTiers[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);
  // champs création
  const [cNom, setCNom] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [cTel, setCTel] = useState('');
  const [saving, setSaving] = useState(false);

  const labelOf = useMemo(() => {
    const m = new Map(TIERS_CATEGORIES_USAGE.map((c) => [c.value, c.label]));
    return (v: TiersCategorieUsage) => m.get(v) ?? v;
  }, []);

  useEffect(() => {
    let annule = false;
    setLoading(true); setError(null);
    const t = setTimeout(() => {
      api.annuaire.listTiers({ recherche: recherche || undefined, categorie: filtre || undefined })
        .then((r) => { if (!annule) setItems(r); })
        .catch((e) => { if (!annule) setError(e instanceof Error ? e.message : 'Erreur'); })
        .finally(() => { if (!annule) setLoading(false); });
    }, 250);
    return () => { annule = true; clearTimeout(t); };
  }, [recherche, filtre]);

  async function creerTiers() {
    if (cNom.trim().length < 2) { setError('Nom trop court'); return; }
    setSaving(true); setError(null);
    try {
      const cats = categorieCreation ?? (categorie ? [categorie] : ['fournisseur_prestataire' as TiersCategorieUsage]);
      const cree = await api.annuaire.createTiers({
        nom: cNom.trim(), email: cEmail.trim() || null, telephone: cTel.trim() || null,
        categoriesUsage: cats,
      });
      // On sélectionne directement le nouveau tiers.
      onSelect({
        id: cree.id, nom: cree.nom, siret: cree.siret, adresse: cree.adresse,
        codePostal: cree.codePostal, ville: cree.ville, email: cree.email,
        telephone: cree.telephone, actif: cree.actif, categoriesUsage: cats,
      });
      setCreation(false); setCNom(''); setCEmail(''); setCTel('');
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-3">
      {/* Recherche + filtre */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
          <Input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher un tiers…" className="pl-8" />
        </div>
        <select value={filtre} onChange={(e) => setFiltre(e.target.value as TiersCategorieUsage | '')}
          className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
          <option value="">Toutes catégories</option>
          {TIERS_CATEGORIES_USAGE.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        {autoriserCreation && (
          <Button type="button" variant="secondary" size="sm" onClick={() => setCreation((v) => !v)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Nouveau
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-danger">{error}</p>}

      {/* Formulaire création à la volée */}
      {creation && (
        <div className="rounded-r-lg border border-border bg-surface/40 p-3 space-y-2">
          <Field label="Nom" htmlFor="tp-nom" required>
            <Input id="tp-nom" value={cNom} onChange={(e) => setCNom(e.target.value)} placeholder="Ex : Entreprise Martin TP" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Email" htmlFor="tp-email"><Input id="tp-email" type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} placeholder="contact@…" /></Field>
            <Field label="Téléphone" htmlFor="tp-tel"><Input id="tp-tel" value={cTel} onChange={(e) => setCTel(e.target.value)} placeholder="06…" /></Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setCreation(false)}>Annuler</Button>
            <Button type="button" variant="primary" size="sm" loading={saving} onClick={() => void creerTiers()}>Créer et sélectionner</Button>
          </div>
        </div>
      )}

      {/* Liste */}
      <ul className="max-h-64 space-y-1 overflow-y-auto">
        {loading && <li className="text-sm text-ink-muted">Chargement…</li>}
        {!loading && items.length === 0 && <li className="text-sm text-ink-subtle">Aucun tiers trouvé.</li>}
        {items.map((t) => {
          const sel = t.id === selectedId;
          return (
            <li key={t.id}>
              <button type="button" onClick={() => onSelect(t)}
                className={`flex w-full items-center justify-between gap-2 rounded-r-md border px-3 py-2 text-left transition-colors ${sel ? 'border-tech bg-tech-tint/30' : 'border-border hover:bg-surface/60'}`}>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-ink">{t.nom}</span>
                  <span className="block truncate text-xs text-ink-subtle">
                    {[t.ville, t.email, t.telephone].filter(Boolean).join(' · ') || '—'}
                  </span>
                  {t.categoriesUsage.length > 0 && (
                    <span className="mt-0.5 flex flex-wrap gap-1">
                      {t.categoriesUsage.map((c) => (
                        <span key={c} className="rounded-r-pill bg-surface px-1.5 py-0.5 text-[9px] uppercase tracking-r-eyebrow text-ink-subtle">{labelOf(c)}</span>
                      ))}
                    </span>
                  )}
                </span>
                {sel && <Check className="h-4 w-4 shrink-0 text-tech" aria-hidden="true" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
